/**
 * Lot BX — messages de confirmation avant un archivage ou une suppression.
 *
 * 🔴 Les anciens textes disaient « DÉFINITIVEMENT » et « irréversible » alors que ces deux
 * actions sont des suppressions douces (le bien passe en `deleted` / `archived` et peut être
 * restauré). Et surtout ils ne prévenaient pas du RETRAIT des annonces : l'utilisateur
 * découvrait après coup que son bien avait disparu de SeLoger.
 *
 * ⛔ On n'invente jamais un état de diffusion : si l'écran ne l'a pas, on le dit au conditionnel.
 */
import { DIFFUSION_SUPPORTS, DIFFUSION_STATUS } from '../constants/diffusionConstants';

const EN_LIGNE = [DIFFUSION_STATUS.DIFFUSE, DIFFUSION_STATUS.EN_ATTENTE];

// ⚠️ Deux écrans, deux formes pour la même information : la liste reçoit de
// `/properties/diffusion-states` un simple mot par support (`en_ligne`, `envoi_en_cours`,
// `modifie_non_renvoye`, `refuse`), la fiche reçoit le bloc `diffusion` complet
// (`{ status, date, url }`). On accepte les deux plutôt que de laisser un écran muet.
const ETATS_LISTE_EN_LIGNE = ['en_ligne', 'envoi_en_cours', 'modifie_non_renvoye'];

const estEnLigne = (valeur) => {
  if (typeof valeur === 'string') return ETATS_LISTE_EN_LIGNE.includes(valeur);
  return EN_LIGNE.includes(valeur?.status);
};

const LIBELLES = {
  seloger: 'SeLoger',
  leboncoin: 'Leboncoin',
  cessionpme: 'CessionPME',
  site_vitrine: 'le site vitrine',
};

const libelle = (id) =>
  LIBELLES[id] || DIFFUSION_SUPPORTS.find(s => s.id === id)?.label || id;

/** Supports sur lesquels le bien est en ligne (diffusé ou en attente), en libellés lisibles. */
export const supportsEnLigne = (etats) => {
  if (!etats || typeof etats !== 'object') return [];
  return Object.entries(etats)
    .filter(([, e]) => estEnLigne(e))
    .map(([id]) => libelle(id));
};

export const estDiffuse = (etats) => supportsEnLigne(etats).length > 0;

const PHRASE_GENERIQUE = 'Si ce bien est diffusé, ses annonces seront retirées des portails.';

/** `etats` absent (information non chargée) → phrase générique, jamais de silence. */
const phraseDiffusion = (etats) => {
  if (etats === undefined || etats === null) return PHRASE_GENERIQUE;
  const supports = supportsEnLigne(etats);
  if (!supports.length) return '';   // ⛔ connu ET non diffusé : on n'ajoute RIEN
  return `Ce bien est diffusé sur : ${supports.join(', ')}. `
    + 'Ses annonces seront retirées de ces portails.';
};

const assembler = (base, etats) => [base, phraseDiffusion(etats)].filter(Boolean).join('\n\n');

/**
 * lot BY — 🔴 un refus du serveur (403 « Seul l'administrateur… », 409 « Archivez d'abord ce
 * bien. ») était noyé sous « Erreur suppression » : l'utilisateur ne pouvait pas savoir qu'il
 * lui manquait un droit ou une étape. On affiche le message du serveur tel quel.
 */
export const messageServeur = (erreur, defaut) => {
  const code = erreur?.response?.status;
  const detail = erreur?.response?.data?.detail;
  return (code === 403 || code === 409) && typeof detail === 'string' && detail ? detail : defaut;
};

export const messageSuppression = (etats) =>
  assembler('Supprimer ce bien ? Il disparaîtra du stock et de la recherche.', etats);

export const messageArchivage = (etats) => assembler('Archiver ce bien ?', etats);

/**
 * Message groupé. `etatsParBien` : { [propertyId]: etats }, ou `undefined` si l'écran ne
 * connaît pas la diffusion (appel en échec, écran legacy) → phrase au conditionnel.
 *
 * ⚠️ Quand la carte des états est connue, un identifiant ABSENT signifie « aucun support
 * engagé » (`/properties/diffusion-states` n'envoie rien pour ces biens) : c'est une
 * information, pas une ignorance. La confondre avec « je ne sais pas » ferait basculer tout un
 * lot sur la phrase générique dès qu'un seul bien n'est diffusé nulle part.
 */
export const messageGroupe = (action, ids, etatsParBien) => {
  const verbe = action === 'archiver' ? 'Archiver' : 'Supprimer';
  const total = ids?.length || 0;
  const base = `${verbe} ${total} bien(s) ?`;
  if (!etatsParBien) return `${base}\n\n${PHRASE_GENERIQUE}`;
  const diffuses = (ids || []).filter(id => estDiffuse(etatsParBien[id])).length;
  if (!diffuses) return base;
  return `${base}\n\n${total} bien(s) sélectionné(s), dont ${diffuses} diffusé(s) : `
    + 'leurs annonces seront retirées des portails.';
};
